import {
  Box,
  Button,
  Center,
  createStyles,
  Group,
  Stack,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import React from 'react';

import {
  DeviceModelBreakdownFormType,
  DeviceModelBreakdownViewEnum,
  useConfig,
} from '@portals/api/organizations';
import { useConfirmationModal } from '@portals/framework';

import { DeviceModelBreakdownForm } from './DeviceModelBreakdownForm';
import { DeviceModelBreakdownWidget } from './DeviceModelBreakdownWidget';
import { WidgetPreviewComponentProps } from '../../../../../modals/WidgetPreview/widget-preview.types';

export function DeviceModelBreakdownWidgetWrapper({
  onClose,
  space,
  onSave,
  type,
  widgetId,
}: WidgetPreviewComponentProps & { onClose: () => void }) {
  const { classes } = useStyles();

  const config = useConfig();

  const deviceModelBreakdownWidgetExtraParams =
    config.data?.space_dashboard?.list.find(
      (item) => item.id === 'devices_model_breakdown' && item.i === widgetId
    )?.extraParams as DeviceModelBreakdownFormType | undefined;

  const getInitialValues = () => {
    const initialValues: DeviceModelBreakdownFormType = {
      title: 'Device model breakdown',
      deviceModelBreakdownView: DeviceModelBreakdownViewEnum.OnlineDevices,
    };

    if (deviceModelBreakdownWidgetExtraParams?.title) {
      initialValues.title = deviceModelBreakdownWidgetExtraParams.title;
    }

    if (deviceModelBreakdownWidgetExtraParams?.deviceModelBreakdownView) {
      initialValues.deviceModelBreakdownView =
        deviceModelBreakdownWidgetExtraParams.deviceModelBreakdownView;
    }

    return initialValues;
  };

  const form = useForm<DeviceModelBreakdownFormType>({
    initialValues: getInitialValues(),
  });

  const asyncConfirmationCheck = useConfirmationModal();

  const onCloseWrapper = async () => {
    if (!form.isDirty()) {
      onClose();
      return;
    }

    const isConfirmed = await asyncConfirmationCheck({
      title: 'Are you sure?',
      description: 'All your changes will be lost',
      confirmationLabel: 'Confirm',
      cancelLabel: 'Cancel',
    });

    if (isConfirmed) {
      onClose();
    }
  };

  const onSubmit = ({
    deviceModelBreakdownView,
    title,
  }: typeof form.values) => {
    onSave?.({
      deviceModelBreakdownView,
      title,
    });

    onClose();
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Box className={classes.container}>
        <Stack className={classes.preview} p="xxl" pb={0} spacing="xxl">
          <Text size={24} color="blue_gray.9">
            Device model breakdown
          </Text>

          <DeviceModelBreakdownForm form={form} />
        </Stack>

        <Center className={classes.config}>
          <DeviceModelBreakdownWidget
            title={form.values.title}
            deviceModelBreakdownView={form.values.deviceModelBreakdownView}
            space={space}
          />
        </Center>

        <Group className={classes.footer} p="xl" position="right">
          <Button variant="default" onClick={onCloseWrapper}>
            Close
          </Button>

          <Button type="submit" data-testid="modal-add-widget-button">
            {type === 'edit' ? 'Save' : 'Add'}
          </Button>
        </Group>
      </Box>
    </form>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr min-content',
    gridTemplateAreas: `
      "preview config"
      "footer footer"
    `,
    gap: 0,
    height: 790,
  },

  preview: {
    gridArea: 'preview',
  },

  config: {
    gridArea: 'config',
    backgroundColor: theme.colors.gray[0],
  },

  footer: {
    gridArea: 'footer',
    borderTop: `1px solid ${theme.colors.gray[2]}`,
  },
}));
